import { Component } from '@theme/component';
import { ThemeEvents } from '@theme/events';

/**
 * The price on a Vitamino PDP is rendered by the Kaching Bundles app block, not by the variant
 * picker, so the bar mirrors the buy box instead of deriving anything from the variant.
 */
const SELECTORS = {
  /** The buy box. Contains both the app block and the product form. */
  root: '.product-details',
  bundleSelected: '.kaching-bundles__bar--selected',
  bundlePrice: '.kaching-bundles__bar-price',
  bundleComparePrice: '.kaching-bundles__bar-full-price',
  priceContainer: '[ref="priceContainer"]',
  price: '.price',
  comparePrice: '.compare-at-price',
  /** Upsell cards and quick-add surfaces sit inside the buy box and carry other products' forms. */
  foreign: 'product-card, .product-card, .resource-card, quick-add-component, quick-add-dialog',
};

/** How long the bar's button stays disabled after an add, if no cart event arrives first. */
const ADD_TO_CART_COOLDOWN = 2000;

/** @param {Element | null | undefined} element @returns {string} */
const textOf = (element) => element?.textContent?.trim() ?? '';

/**
 * `.targeted-customer { display: none }` (sections/nutritional-formula.liquid) is how the
 * Nährstoffformel gate hides the buy box and the price from customers without an order number —
 * the button stays in the DOM and stays enabled, so `disabled` alone does not see the gate.
 * `offsetParent` catches that and every other `display: none` ancestor; the buy box is never
 * positioned fixed, so that caveat of `offsetParent` does not apply here.
 *
 * @param {Element | null | undefined} element
 */
const isVisible = (element) => element instanceof HTMLElement && element.offsetParent !== null;

/** @param {string | null | undefined} value @param {number} fallback */
const toInteger = (value, fallback) => {
  const parsed = parseInt(value ?? '', 10);

  return Number.isNaN(parsed) ? fallback : parsed;
};

/**
 * @typedef {object} StickyAddToCartRefs
 * @property {HTMLElement} currentPrice
 * @property {HTMLElement} comparePriceGroup
 * @property {HTMLElement} comparePrice
 * @property {HTMLButtonElement} addToCartButton
 * @property {HTMLElement} [salePriceLabel]
 * @property {HTMLInputElement} [quantityInput]
 *
 * @extends Component<StickyAddToCartRefs>
 */
class StickyAddToCartComponent extends Component {
  requiredRefs = ['currentPrice', 'comparePriceGroup', 'comparePrice', 'addToCartButton'];

  /** @type {AbortController | undefined} */
  #abortController;

  /** @type {number | undefined} */
  #syncFrame;

  /** @type {number | undefined} */
  #cooldownTimeout;

  /** Set while an add is in flight, so a double tap cannot post to /cart/add twice. */
  #cooldown = false;

  /** Set while the buy box is absent or hidden — keeps the bar off screen entirely. */
  #blocked = false;

  /** Set while a selected Kaching bundle owns the quantity, so the bar must not write to it. */
  #bundleQuantity = false;

  #observer = new MutationObserver(() => this.#scheduleSync());

  connectedCallback() {
    super.connectedCallback();

    // Recreated per connection: the controller is aborted on disconnect, and reusing an aborted
    // signal would silently drop every listener after a theme editor reorder or a morph.
    this.#abortController = new AbortController();

    const { signal } = this.#abortController;

    window.addEventListener('scroll', this.#onScroll, { passive: true, signal });
    // Kept alongside the observer so variant picker changes apply on the same tick as the theme's.
    document.addEventListener(ThemeEvents.variantUpdate, this.#onVariantUpdate, { signal });
    document.addEventListener(ThemeEvents.cartUpdate, this.#endCooldown, { signal });
    document.addEventListener(ThemeEvents.cartError, this.#endCooldown, { signal });

    const root = this.#root;

    if (root) {
      this.#observer.observe(root, {
        subtree: true,
        childList: true,
        characterData: true,
        attributes: true,
        attributeFilter: ['class', 'disabled'],
      });
    }

    this.#sync();
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#observer.disconnect();
    this.#abortController?.abort();

    if (this.#syncFrame) cancelAnimationFrame(this.#syncFrame);
    if (this.#cooldownTimeout) clearTimeout(this.#cooldownTimeout);
  }

  decreaseQuantity() {
    this.#setQuantity(this.#quantity - this.#quantityRules.step);
  }

  increaseQuantity() {
    this.#setQuantity(this.#quantity + this.#quantityRules.step);
  }

  clampQuantity() {
    // Reads the bar's own input rather than #quantity, which prefers the form: this runs on the
    // bar's change event, where the typed value is the one that needs clamping.
    this.#setQuantity(toInteger(this.refs.quantityInput?.value, NaN));
  }

  addToCart() {
    const button = this.#mainAddToCartButton;

    if (!button || button.disabled || !isVisible(button)) return;
    if (this.#cooldown) return;

    // Neither this method nor the theme's handleSubmit disables anything while the request is in
    // flight, so a double tap on mobile would otherwise add the item twice.
    this.#cooldown = true;
    this.#cooldownTimeout = setTimeout(this.#endCooldown, ADD_TO_CART_COOLDOWN);

    // The form already holds the quantity, set either by the bundle app or by the bar's own
    // stepper. Writing it again here would clobber a bundle quantity when the bar has no stepper.
    button.click();

    this.#scheduleSync();
  }

  get #root() {
    return document.querySelector(SELECTORS.root) ?? this.#mainForm?.closest('.shopify-section') ?? null;
  }

  get #mainForm() {
    const { productId } = this.dataset;

    if (!productId) return null;

    const selector = `product-form-component[data-product-id="${productId}"]`;
    // Prefer the buy box. A bare document lookup can land on a quick-add dialog's form, which is
    // hidden until opened and would read as a hidden buy box.
    const form = document.querySelector(SELECTORS.root)?.querySelector(selector) ?? document.querySelector(selector);

    return form instanceof HTMLElement && !form.closest(SELECTORS.foreign) ? form : null;
  }

  get #mainAddToCartButton() {
    const button = this.#mainForm?.querySelector('[ref="addToCartButton"]');

    return button instanceof HTMLButtonElement ? button : null;
  }

  get #mainQuantityInput() {
    const input = this.#mainForm?.querySelector('input[name="quantity"]');

    return input instanceof HTMLInputElement ? input : null;
  }

  get #quantity() {
    // The form's input is the source of truth: the bundle app writes to it directly, so stepping
    // from the bar's own value could silently lower a quantity the bundle had set.
    const input = this.#mainQuantityInput ?? this.refs.quantityInput;

    return toInteger(input?.value, this.#quantityRules.min);
  }

  /**
   * Mirrors the variant's quantity rules off the main input. The bar's own input is not validated
   * by quantity-selector-component — that component has no change listener — and the product form
   * is novalidate, so anything the bar writes reaches /cart/add unchecked.
   */
  get #quantityRules() {
    const input = this.#mainQuantityInput;
    const min = Math.max(1, toInteger(input?.min, 1));
    const max = toInteger(input?.max, NaN);
    const step = Math.max(1, toInteger(input?.step, 1));

    return { min, max: Number.isNaN(max) ? Infinity : Math.max(min, max), step };
  }

  /** @param {number} value @returns {number} */
  #clamp(value) {
    const { min, max, step } = this.#quantityRules;

    if (!Number.isFinite(value)) return min;

    // Snap to the increment the way stepUp/stepDown do — the step base of a number input is its
    // min — so a typed 1.5 or an off-increment value cannot reach the cart.
    const steps = Math.round((value - min) / step);

    return Math.min(max, Math.max(min, min + steps * step));
  }

  /**
   * The form's input is what the cart request reads, so the bar has to write through to it.
   *
   * @param {number} value
   */
  #setQuantity(value) {
    const { quantityInput } = this.refs;

    if (!quantityInput) return;

    // A selected bundle owns the quantity and prices it as a package. Writing through would leave
    // the bar showing a bundle price for a quantity the bundle never set.
    if (this.#bundleQuantity) return;

    const quantity = String(this.#clamp(value));
    const mainQuantityInput = this.#mainQuantityInput;

    quantityInput.value = quantity;

    if (mainQuantityInput && mainQuantityInput.value !== quantity) {
      mainQuantityInput.value = quantity;
      mainQuantityInput.dispatchEvent(new Event('change', { bubbles: true }));
    }
  }

  #onScroll = () => {
    this.#updateVisibility();
  };

  #updateVisibility() {
    const pastTrigger = window.scrollY > Number(this.dataset.triggerOffset || 0);

    this.classList.toggle('sticky-atc--visible', pastTrigger && !this.#blocked);
  }

  /** @param {Event} event */
  #onVariantUpdate = (event) => {
    // Mirrors product-form.js and product-price.js: on a combined listing the product itself
    // changes, and without adopting the new id #mainForm stops resolving and the price lookup
    // stops matching, leaving a dead button next to a stale price.
    const newProduct = /** @type {any} */ (event).detail?.data?.newProduct;

    if (newProduct?.id) this.dataset.productId = String(newProduct.id);

    this.#scheduleSync();
  };

  #endCooldown = () => {
    if (this.#cooldownTimeout) clearTimeout(this.#cooldownTimeout);

    this.#cooldownTimeout = undefined;
    this.#cooldown = false;

    this.#scheduleSync();
  };

  #scheduleSync = () => {
    if (this.#syncFrame) cancelAnimationFrame(this.#syncFrame);

    this.#syncFrame = requestAnimationFrame(() => this.#sync());
  };

  #sync() {
    this.#syncFrame = undefined;

    const price = this.#readPrice();

    this.#bundleQuantity = price?.fromBundle === true;
    this.classList.toggle('sticky-atc--bundle-quantity', this.#bundleQuantity);

    if (price) {
      const { currentPrice, comparePriceGroup, comparePrice, salePriceLabel } = this.refs;
      const onSale = price.compareAt !== '';

      if (textOf(currentPrice) !== price.current) currentPrice.textContent = price.current;
      if (textOf(comparePrice) !== price.compareAt) comparePrice.textContent = price.compareAt;

      comparePriceGroup.hidden = !onSale;

      // Announcing "sale price" for an undiscounted product misreads it, so the label follows the
      // compare-at price the way snippets/price.liquid does.
      if (salePriceLabel) salePriceLabel.hidden = !onSale;
    }

    const mainButton = this.#mainAddToCartButton;

    // No buy box, or one hidden behind the Nährstoffformel gate: the bar must not offer an add,
    // and must not show a price the page is deliberately withholding.
    this.#blocked = !mainButton || !isVisible(mainButton);
    this.#updateVisibility();

    if (mainButton) {
      const { addToCartButton } = this.refs;

      addToCartButton.disabled = mainButton.disabled || this.#cooldown;
      addToCartButton.classList.toggle('sticky-atc__button--sold-out', mainButton.disabled);
    }

    this.#syncQuantity();
  }

  #syncQuantity() {
    const mainQuantityInput = this.#mainQuantityInput;
    const { quantityInput } = this.refs;

    if (!mainQuantityInput || !quantityInput) return;

    const { min, max, step } = this.#quantityRules;

    if (quantityInput.min !== String(min)) quantityInput.min = String(min);
    if (quantityInput.step !== String(step)) quantityInput.step = String(step);

    if (Number.isFinite(max)) {
      if (quantityInput.max !== String(max)) quantityInput.max = String(max);
    } else if (quantityInput.hasAttribute('max')) {
      quantityInput.removeAttribute('max');
    }

    if (mainQuantityInput.value !== quantityInput.value) quantityInput.value = mainQuantityInput.value;
  }

  /**
   * @returns {{ current: string, compareAt: string, fromBundle: boolean } | null} Null when the
   *   page renders no price of its own, in which case the server rendered value stands.
   */
  #readPrice() {
    const root = this.#root;

    if (!root) return null;

    const bundle = root.querySelector(SELECTORS.bundleSelected);

    if (bundle && isVisible(bundle)) {
      const current = textOf(bundle.querySelector(SELECTORS.bundlePrice));

      if (current) {
        return {
          current,
          compareAt: textOf(bundle.querySelector(SELECTORS.bundleComparePrice)),
          fromBundle: true,
        };
      }
    }

    const { productId } = this.dataset;

    for (const productPrice of root.querySelectorAll(`product-price[data-product-id="${productId}"]`)) {
      if (productPrice.closest(SELECTORS.foreign)) continue;
      // The gate hides the price block for customers without an order number.
      if (!isVisible(productPrice)) continue;

      const container = productPrice.querySelector(SELECTORS.priceContainer);
      const current = textOf(container?.querySelector(SELECTORS.price));

      if (current) {
        return {
          current,
          compareAt: textOf(container?.querySelector(SELECTORS.comparePrice)),
          fromBundle: false,
        };
      }
    }

    return null;
  }
}

if (!customElements.get('sticky-add-to-cart-component')) {
  customElements.define('sticky-add-to-cart-component', StickyAddToCartComponent);
}
